<div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">

  <!-- Save/status feedback: fixed bottom-right toast so it is always visible
       regardless of scroll position (the inline header banner was easy to miss). -->
  <div
    *ngIf="message"
    class="fixed bottom-6 right-6 z-50 rounded-lg bg-emerald-600 px-5 py-3 text-sm font-medium text-white shadow-xl ring-1 ring-emerald-700/20"
    role="status"
    aria-live="polite"
  >
    {{ message }}
  </div>

  <!-- Gallery lightbox: click an image to view it large, navigate with arrows -->
  <div
    *ngIf="lightbox.open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 select-none"
    role="dialog"
    aria-modal="true"
    (click)="lightboxClose()"
  >
    <button
      type="button"
      (click)="lightboxClose()"
      title="Close (Esc)"
      class="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white hover:bg-white/20"
    >&times;</button>

    <button
      type="button"
      (click)="lightboxPrev(); $event.stopPropagation()"
      title="Previous (←)"
      class="absolute left-3 sm:left-6 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-3xl leading-none text-white hover:bg-white/20"
    >&#8249;</button>

    <button
      type="button"
      (click)="lightboxNext(); $event.stopPropagation()"
      title="Next (→)"
      class="absolute right-3 sm:right-6 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-3xl leading-none text-white hover:bg-white/20"
    >&#8250;</button>

    <figure class="flex max-h-[90vh] max-w-[90vw] flex-col items-center" (click)="$event.stopPropagation()">
      <img
        *ngIf="lightboxCurrent() as cur"
        [src]="galleryUrl(cur)"
        [alt]="cur.name"
        class="max-h-[80vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
      />
      <figcaption class="mt-3 text-center text-sm text-white/80">
        <span *ngIf="lightboxCurrent() as cur">{{ cur.name }}</span>
        <span class="ml-2 text-white/50">{{ lightbox.index + 1 }} / {{ lightbox.images.length }}</span>
      </figcaption>
    </figure>
  </div>

  <!-- Reusable confirmation dialog (e.g. deleting a gallery image) -->
  <div
    *ngIf="confirmDialog.open"
    class="fixed inset-0 z-50 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
  >
    <div class="absolute inset-0 bg-gray-900/50" (click)="confirmDialogCancel()"></div>
    <div class="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
      <div class="p-6">
        <div class="flex items-start gap-4">
          <div
            class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full"
            [class.bg-red-100]="confirmDialog.tone === 'danger'"
            [class.bg-blue-100]="confirmDialog.tone !== 'danger'"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              [class.text-red-600]="confirmDialog.tone === 'danger'"
              [class.text-blue-600]="confirmDialog.tone !== 'danger'"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-base font-semibold text-gray-900">{{ confirmDialog.title }}</h3>
            <p class="mt-1 text-sm text-gray-500">{{ confirmDialog.message }}</p>
            <img
              *ngIf="confirmDialog.imageUrl"
              [src]="confirmDialog.imageUrl"
              class="mt-3 h-28 w-auto rounded-lg border border-gray-200 object-cover"
              alt=""
            />
          </div>
        </div>
      </div>
      <div class="flex justify-end gap-3 bg-gray-50 px-6 py-4">
        <button type="button" (click)="confirmDialogCancel()" class="ec-btn-secondary">
          {{ confirmDialog.cancelLabel }}
        </button>
        <button
          type="button"
          (click)="confirmDialogConfirm()"
          class="inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm"
          [class.bg-red-600]="confirmDialog.tone === 'danger'"
          [class.hover:bg-red-700]="confirmDialog.tone === 'danger'"
          [class.bg-blue-700]="confirmDialog.tone !== 'danger'"
          [class.hover:bg-blue-800]="confirmDialog.tone !== 'danger'"
        >
          {{ confirmDialog.confirmLabel }}
        </button>
      </div>
    </div>
  </div>

  <div class="flex items-center justify-between mb-4">
    <h1 class="text-lg font-semibold text-gray-800">
      Event Configuration
      <span *ngIf="client" class="ml-2 text-sm font-normal text-gray-400">{{ client }}</span>
    </h1>
    <div class="flex items-center gap-3">
      <button *ngIf="!loading" type="button" (click)="toggleImport()" class="ec-btn-secondary">
        {{ importOpen ? 'Close import' : 'Import / load config' }}
      </button>
    </div>
  </div>

  <div *ngIf="loading" class="text-sm text-gray-500">Loading…</div>

  <!-- Import / bootstrap from an old config (toggled panel, not a tab) -->
  <div
    *ngIf="!loading && importOpen"
    class="mb-4 rounded-xl bg-white shadow-sm ring-1 ring-blue-900/10 overflow-hidden"
  >
    <div class="p-6 space-y-4">
      <div>
        <h3 class="text-base font-bold text-gray-900">Bootstrap from an existing config</h3>
        <p class="mt-1 text-xs text-gray-500 max-w-2xl">
          Avoid retyping everything: load an old configuration file and the form is
          pre-filled automatically. Supported files (drop the whole file in):
        </p>
        <ul class="mt-1 ml-4 list-disc text-xs text-gray-500 max-w-2xl">
          <li><code>config/clients/&lt;client&gt;.json</code> — event details, logistics, agenda days, PDF images</li>
          <li><code>webapp/src/assets/clients/&lt;client&gt;.json</code> — welcome text, hero video, languages, hotel/dates</li>
          <li><code>webapp/src/environments/environment.*.ts</code> — title, domain, logistics, languages, categories, dynamic fields</li>
          <li><code>*_conf.inc.php</code> — full legacy config: flags, notifications, test-mail, activities, agenda, dynamic fields</li>
        </ul>
        <p class="mt-1 text-xs text-gray-400">Nothing is saved automatically — review each tab, then press “Save everything”.</p>
      </div>

      <label class="block">
        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Upload a config file (.json, .ts, .php)</span>
        <input type="file" accept="application/json,.json,.ts,.php,.inc" (change)="onImportFile($event)" class="mt-1 block text-sm text-gray-600" />
      </label>

      <label class="block">
        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">…or paste the file content</span>
        <textarea
          [(ngModel)]="importText"
          rows="8"
          class="ec-input font-mono text-xs"
          [placeholder]="importPlaceholder"
        ></textarea>
      </label>

      <div class="flex items-center gap-3">
        <button (click)="applyImport()" class="ec-btn-secondary">Analyze &amp; pre-fill</button>
        <button (click)="saveAll()" class="ec-btn">Save everything</button>
        <span *ngIf="importMessage" class="text-xs text-gray-600">{{ importMessage }}</span>
      </div>
    </div>
  </div>

  <div *ngIf="!loading">

    <!-- Event lifecycle phase bar (tab-like; selecting only switches the panel) -->
    <div class="ec-phasebar" role="tablist" aria-label="Event phase">
      <button
        *ngFor="let p of phases"
        type="button"
        role="tab"
        class="ec-phase"
        [class.is-active]="activePhase === p.id"
        [attr.aria-selected]="activePhase === p.id"
        [style.--ec-phase-color]="p.color"
        (click)="activePhase = p.id"
      >
        <span class="ec-phase-dot" [style.backgroundColor]="p.color"></span>
        {{ p.label }}
      </button>
    </div>

    <!-- Per-phase quick-control panel -->
    <div class="ec-phase-panel" [ngSwitch]="activePhase">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-base font-bold text-gray-900">
          {{ activePhase === 'teaser' ? 'Teaser' :
             activePhase === 'test' ? 'Test run' :
             activePhase === 'pre-event' ? 'Pre-event' :
             activePhase === 'live' ? 'Event LIVE' : 'Post-event' }}
        </h3>
        <button type="button" (click)="savePhase()" class="ec-btn">Set as current phase</button>
      </div>

      <!-- Teaser -->
      <div *ngSwitchCase="'teaser'" class="space-y-3">
        <p class="text-sm text-gray-600">Pre-launch teaser page is live; registration is not open yet.</p>
        <button type="button" (click)="goToTab('Content')" class="ec-btn-secondary">Manage teaser content</button>
      </div>

      <!-- Test run -->
      <div *ngSwitchCase="'test'" class="space-y-3">
        <p class="text-sm text-gray-600">Internal test mode for previewing the event setup.</p>
        <label class="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" [(ngModel)]="flags.test_version" />
          Test version active
        </label>
        <div>
          <button type="button" (click)="goToTab('Operations')" class="ec-btn-secondary">Manage in Operations</button>
        </div>
      </div>

      <!-- Pre-event -->
      <div *ngSwitchCase="'pre-event'" class="space-y-3">
        <p class="text-sm text-gray-600">Registration and booking open in the run-up to the event.</p>
        <div class="flex flex-col gap-2">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" [(ngModel)]="visitorBookingActive" />
            Visitor booking active
          </label>
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" [(ngModel)]="exhibitorBookingActive" />
            Exhibitor booking active
          </label>
        </div>
        <div>
          <button type="button" (click)="goToTab('Operations')" class="ec-btn-secondary">Manage in Operations</button>
        </div>
      </div>

      <!-- Event LIVE -->
      <div *ngSwitchCase="'live'" class="space-y-3">
        <p class="text-sm text-gray-600">
          Event is running. Bookings are currently
          <strong>{{ flags.booking_stop ? 'stopped' : 'open' }}</strong> for visitors and exhibitors.
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <button type="button" (click)="goLive()" class="ec-btn">Stop bookings &amp; go live</button>
          <button type="button" (click)="reopenBooking()" class="ec-btn-secondary">Re-open bookings</button>
          <button type="button" (click)="goToTab('Operations')" class="ec-btn-secondary">Manage in Operations</button>
        </div>
      </div>

      <!-- Post-event -->
      <div *ngSwitchCase="'post-event'" class="space-y-3">
        <p class="text-sm text-gray-600">Event finished; gallery and recap content are shown.</p>
        <label class="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" [(ngModel)]="flags.show_gallery" />
          Show gallery
        </label>
        <div>
          <button type="button" (click)="goToTab('Gallery')" class="ec-btn-secondary">Manage gallery</button>
        </div>
      </div>
    </div>

    <mat-tab-group class="ec-tabs" [(selectedIndex)]="mainTabIndex">

      <!-- Event -->
      <mat-tab label="Event">
        <div class="p-6 space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</span>
              <input [(ngModel)]="event.title" class="ec-input" placeholder="Bonjour France 2026" />
              <span class="ec-hint">The public event name shown in browser titles and emails. e.g. “Bonjour France 2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Year</span>
              <input [(ngModel)]="event.year" class="ec-input" placeholder="2026" />
              <span class="ec-hint">Edition year, used in PDFs and account info. e.g. “2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Domain (www_url)</span>
              <input [(ngModel)]="event.www_url" class="ec-input" placeholder="www.bonjour-frankreich.fr" />
              <span class="ec-hint">Public domain without protocol. e.g. “www.bonjour-frankreich.fr”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Frontend URL</span>
              <input [(ngModel)]="event.frontend_url" class="ec-input" placeholder="https://bonjour-frankreich.fr" />
              <span class="ec-hint">Full URL (with https://) used in links and emails. e.g. “https://bonjour-frankreich.fr”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Info email</span>
              <input [(ngModel)]="event.email" class="ec-input" placeholder="info@bonjour-frankreich.fr" />
              <span class="ec-hint">Contact / reply-to address for participants. e.g. “info@bonjour-frankreich.fr”.</span>
            </label>
          </div>
          <!-- div, not label: a label wrapping the contenteditable steals focus. -->
          <div class="block">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Welcome heading</span>
            <app-inline-rich-edit
              [(ngModel)]="event.welcome_heading"
              [ngModelOptions]="{ standalone: true }"
              placeholder="Bonjour Frankreich"
              class="ec-input"
            ></app-inline-rich-edit>
            <span class="ec-hint">The big heading (&lt;h1&gt;) at the top of the public welcome dialog. Press Enter for a line break (&lt;br&gt;). The edition year is appended automatically. e.g. “Bonjour&lt;br&gt;Frankreich”.</span>
          </div>
          <!-- NOT a <label>: a <label> wrapping a contenteditable steals the click
               and bounces focus, so the editor was untypable. A plain div fixes it. -->
          <div class="block">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Welcome text</span>
            <app-inline-rich-edit
              [(ngModel)]="event.welcome"
              [ngModelOptions]="{ standalone: true }"
              placeholder="The annual event dedicated to French culture — from 30 Sept to 2 Oct 2026 in Nîmes…"
              class="ec-input"
            ></app-inline-rich-edit>
            <span class="ec-hint">The body shown below the heading on the public welcome dialog. Click to edit; select text for bold, italic, underline or a link. Takes effect after the public app reloads.</span>
          </div>
          <div class="pt-2">
            <button (click)="saveEvent()" class="ec-btn">Save event</button>
          </div>
        </div>
      </mat-tab>

      <!-- Content (Phase 2 — WYSIWYG) -->
      <mat-tab label="Content">
        <ng-template matTabContent>
          <div class="p-6 space-y-6">
            <p class="text-xs text-gray-400">
              Rich text per active language. Pick a page below, then edit its content.
              Images are uploaded to the event asset store. Leaving a block empty keeps the
              page's built-in content.
            </p>

            <!-- One sub-tab per editable page; navigated via the dropdown. -->
            <div class="ec-subnav">
              <label class="ec-subnav-label">Page</label>
              <select
                class="ec-input ec-subnav-select"
                [(ngModel)]="contentTabIndex"
                [ngModelOptions]="{ standalone: true }"
              >
                <option *ngFor="let section of contentSections; let i = index" [ngValue]="i">
                  {{ section.label }}
                </option>
              </select>
            </div>
            <mat-tab-group
              class="ec-subtabs ec-subtabs--dropdown"
              mat-stretch-tabs="false"
              animationDuration="0ms"
              [(selectedIndex)]="contentTabIndex"
            >
              <mat-tab *ngFor="let section of contentSections" [label]="section.label">
                <ng-template matTabContent>
                  <div class="pt-5">
                    <h3 class="text-base font-bold text-gray-900 mb-3">
                      {{ section.label }}
                      <span class="text-gray-400 font-normal">({{ section.key }})</span>
                    </h3>

                    <!-- Prose section — authored in the page's single role language -->
                    <div *ngIf="section.kind === 'prose'" class="space-y-5">
                      <ng-container *ngIf="sectionLang(section) as l">
                        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">{{ sectionLangLabel(section) }}</span>
                        <editor
                          [init]="editorInit"
                          [(ngModel)]="proseModel(section.key, l).html"
                          [ngModelOptions]="{ standalone: true }"
                        ></editor>
                      </ng-container>
                    </div>

                    <!-- Program section: one tab per day, structured agenda rows -->
                    <div *ngIf="section.kind === 'program'">
                      <ng-container *ngIf="sectionLang(section) as l">
                        <div class="flex items-center justify-between mb-2 mt-4">
                          <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">{{ sectionLangLabel(section) }}</span>
                        </div>

                        <!-- Day selector (each day is a tab; no reordering) -->
                        <div class="flex flex-wrap items-center gap-2 mb-3 border-b border-gray-200 pb-2">
                          <button
                            *ngFor="let tab of programTabs(section.key, l); let i = index; trackBy: trackByIndex"
                            (click)="selectProgramDay(section.key, i)"
                            class="rounded-t px-3 py-1.5 text-sm border-b-2"
                            [ngClass]="activeProgramDay(section.key) === i
                              ? 'border-blue-500 text-blue-700 bg-blue-50 font-medium'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
                          >
                            {{ tab.title || 'Day ' + (i + 1) }}
                          </button>
                          <button (click)="addTab(section.key, l)" class="ec-btn-secondary ml-1">+ Add day</button>
                        </div>

                        <!-- Active day editor -->
                        <ng-container *ngIf="activeTab(section.key) as tab">
                          <div class="rounded border border-gray-200 border-l-4 border-l-blue-300 bg-blue-50/40 p-3 mb-3">
                            <div class="flex items-center gap-2 mb-3">
                              <input
                                [(ngModel)]="tab.title"
                                [ngModelOptions]="{ standalone: true }"
                                placeholder="Date (e.g. 24.09.2024)"
                                class="ec-input ec-day-title"
                              />
                              <input
                                [(ngModel)]="tab.heading"
                                [ngModelOptions]="{ standalone: true }"
                                placeholder="Day heading (e.g. Wednesday, 25 September 2024)"
                                class="ec-input ec-slot-text"
                              />
                              <button
                                (click)="removeTab(section.key, l, activeProgramDay(section.key))"
                                class="text-xs text-red-500 hover:text-red-700 px-2"
                                title="Remove this day"
                              >Remove day</button>
                            </div>

                            <!-- Slots: each is a compact time + content row -->
                            <div
                              *ngFor="let item of tab.items; let r = index; trackBy: trackByIndex"
                              class="flex items-start gap-2 mb-1.5"
                            >
                              <input
                                [(ngModel)]="item.time"
                                [ngModelOptions]="{ standalone: true }"
                                placeholder="Time"
                                class="ec-input ec-slot-time"
                              />
                              <app-inline-rich-edit
                                [(ngModel)]="item.text"
                                [ngModelOptions]="{ standalone: true }"
                                placeholder="Content"
                                class="ec-input ec-slot-text"
                              ></app-inline-rich-edit>
                              <div class="flex flex-none items-center gap-1">
                                <button (click)="moveAgendaItem(tab, r, -1)" class="ec-btn-secondary px-2" title="Move up">↑</button>
                                <button (click)="moveAgendaItem(tab, r, 1)" class="ec-btn-secondary px-2" title="Move down">↓</button>
                                <button (click)="removeAgendaItem(tab, r)" class="text-xs text-red-500 hover:text-red-700 px-2" title="Remove slot">✕</button>
                              </div>
                            </div>

                            <p *ngIf="!tab.items?.length" class="text-xs text-gray-400 mb-2">
                              No slots yet — add a slot below.
                            </p>

                            <div class="flex items-center gap-2 mt-2">
                              <button (click)="addAgendaItem(tab)" class="ec-btn-secondary">+ Add slot</button>
                            </div>
                          </div>
                        </ng-container>

                        <p *ngIf="programTabs(section.key, l).length === 0" class="text-xs text-gray-400">
                          No days yet — add a day to build the program.
                        </p>
                      </ng-container>
                    </div>
                  </div>
                </ng-template>
              </mat-tab>
            </mat-tab-group>

            <div class="pt-2 flex items-center gap-3">
              <button (click)="saveContent()" [disabled]="contentSaving" class="ec-btn">
                {{ contentSaving ? 'Saving…' : 'Save content' }}
              </button>
              <span
                *ngIf="contentStatus"
                class="text-sm font-medium"
                [class.text-emerald-600]="!contentStatusError"
                [class.text-red-600]="contentStatusError"
              >
                {{ contentStatus }}
              </span>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Legal -->
      <mat-tab label="Legal">
        <ng-template matTabContent>
          <div class="p-6 space-y-6">
            <p class="text-xs text-gray-400 max-w-3xl">
              Consent texts shown on the registration "Terms" / "Conditions" step. Visitor texts are
              authored in the visitor language and exhibitor texts in the exhibitor language (set in the
              Languages tab) — only that language is shown here. Toggle a block off to hide its checkbox
              in the public form. Leaving a text empty keeps the form's built-in wording.
            </p>

            <!-- Visitor / Exhibitor sub-tabs: each shows only its role language. -->
            <mat-tab-group
              class="ec-subtabs"
              mat-stretch-tabs="false"
              animationDuration="0ms"
              [(selectedIndex)]="legalGroupIndex"
            >
              <mat-tab *ngFor="let group of legalGroups" [label]="group.label">
                <ng-template matTabContent>
                  <div class="pt-5 space-y-5">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {{ group.label }} language
                      </span>
                      <span class="ec-legal-langbadge">{{ legalLangLabel(group.role) }}</span>
                    </div>

                    <div
                      *ngFor="let s of group.sections"
                      class="ec-legal-card"
                      [ngClass]="'ec-legal-card--' + legalTone(s.type)"
                    >
                      <div class="ec-legal-card__head">
                        <div>
                          <span class="text-sm font-semibold text-gray-900">{{ s.label }}</span>
                          <span class="text-xs text-gray-400 ml-2">({{ legalBlockKey(s.role, s.type) }})</span>
                        </div>
                        <label class="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            [ngModel]="legalShow(s.flag)"
                            (ngModelChange)="setLegalShow(s.flag, $event)"
                            [ngModelOptions]="{ standalone: true }"
                            [disabled]="frontendDatesReadonly || !frontendDatesLoaded"
                          />
                          Show in form
                        </label>
                      </div>

                      <div class="ec-legal-card__body">
                        <div *ngFor="let l of legalLangs(group.role)" class="space-y-1">
                          <editor
                            [init]="legalEditorInit"
                            [(ngModel)]="legalModel(s.role, s.type, l).html"
                            [ngModelOptions]="{ standalone: true }"
                          ></editor>
                        </div>
                      </div>
                    </div>
                  </div>
                </ng-template>
              </mat-tab>
            </mat-tab-group>

            <p *ngIf="frontendDatesReadonly || !frontendDatesLoaded" class="text-xs text-amber-600">
              The client config file is not writable from here on this deployment, so the show/hide flags
              cannot be changed — edit them directly in the client JSON. The texts above still save.
            </p>

            <div class="pt-2 flex items-center gap-3">
              <button (click)="saveLegal()" [disabled]="legalSaving" class="ec-btn">
                {{ legalSaving ? 'Saving…' : 'Save legal' }}
              </button>
              <span
                *ngIf="legalStatus"
                class="text-sm font-medium"
                [class.text-emerald-600]="!legalStatusError"
                [class.text-red-600]="legalStatusError"
              >
                {{ legalStatus }}
              </span>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Agenda -->
      <mat-tab label="Agenda">
        <div class="p-6 space-y-4">
          <p class="text-xs text-gray-500 max-w-2xl">
            Define each meeting day and its appointment slots. Slots are generated from start/end + interval,
            minus any breaks. “Spots” is how many parallel meetings can run per slot (0 = unlimited).
          </p>
          <div *ngFor="let day of days; let i = index" class="rounded-lg border border-gray-200 p-4">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <span class="text-base font-bold text-gray-900">Day {{ day.day_no }}</span>
                <label class="inline-flex items-center gap-1 text-xs text-gray-500">
                  <input type="checkbox" [checked]="day.enabled === 1"
                    (change)="day.enabled = $any($event.target).checked ? 1 : 0" />
                  enabled
                </label>
              </div>
              <button (click)="removeDay(i)" class="text-xs text-red-500 hover:text-red-700">Remove</button>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label class="block">
                <span class="text-xs text-gray-500">Caption</span>
                <input [(ngModel)]="day.caption" [ngModelOptions]="{ standalone: true }" class="ec-input" placeholder="Marseille — Day 1" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Date</span>
                <input type="date" [(ngModel)]="day.event_date" [ngModelOptions]="{ standalone: true }" class="ec-input" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Meeting spots</span>
                <input type="number" [(ngModel)]="day.spots" [ngModelOptions]="{ standalone: true }" class="ec-input" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Slot start</span>
                <input type="time" [(ngModel)]="day.slot_start" [ngModelOptions]="{ standalone: true }" class="ec-input" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Slot end</span>
                <input type="time" [(ngModel)]="day.slot_end" [ngModelOptions]="{ standalone: true }" class="ec-input" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Interval (min)</span>
                <input type="number" [(ngModel)]="day.slot_interval" [ngModelOptions]="{ standalone: true }" class="ec-input" />
              </label>
              <label class="block sm:col-span-2">
                <span class="text-xs text-gray-500">Breaks (comma separated HH:MM)</span>
                <input [(ngModel)]="day.breaks" [ngModelOptions]="{ standalone: true }" class="ec-input" placeholder="12:30, 12:45, 13:00, 13:15" />
              </label>
              <label class="block">
                <span class="text-xs text-gray-500">Break caption</span>
                <input [(ngModel)]="day.break_caption" [ngModelOptions]="{ standalone: true }" class="ec-input" placeholder="Lunch" />
              </label>
            </div>
          </div>

          <div class="flex items-center gap-3 pt-2">
            <button (click)="addDay()" class="ec-btn-secondary">Add day</button>
            <button (click)="saveAgenda()" class="ec-btn">Save agenda</button>
          </div>

          <!-- Shared desk pairs -->
          <section class="rounded-lg border border-gray-200 p-4 mt-4">
            <div class="flex items-center justify-between mb-2">
              <div>
                <h3 class="text-base font-bold text-gray-900">Shared desk pairs</h3>
                <p class="ec-hint">
                  Link two exhibitors from different companies who sit at the same desk and share one
                  agenda. Booking a slot with either one automatically books it with the other; a
                  cancellation frees the slot for both. Each exhibitor can be in one pair only.
                </p>
              </div>
              <button (click)="addDeskPair()" class="ec-btn-secondary">Add pair</button>
            </div>

            <div class="space-y-2 max-w-3xl">
              <div
                *ngFor="let pair of deskPairs; let i = index; trackBy: trackByIndex"
                class="flex items-center gap-2"
              >
                <span class="w-6 text-xs text-gray-400">{{ i + 1 }}</span>
                <select [(ngModel)]="pair.a" [ngModelOptions]="{ standalone: true }" class="ec-input flex-1">
                  <option [ngValue]="0">— select exhibitor —</option>
                  <option *ngFor="let o of exhibitorOptions" [ngValue]="o.id">{{ o.label }}</option>
                </select>
                <span class="text-xs text-gray-400">↔</span>
                <select [(ngModel)]="pair.b" [ngModelOptions]="{ standalone: true }" class="ec-input flex-1">
                  <option [ngValue]="0">— select exhibitor —</option>
                  <option *ngFor="let o of exhibitorOptions" [ngValue]="o.id">{{ o.label }}</option>
                </select>
                <button (click)="removeDeskPair(i)" class="text-xs text-red-500 hover:text-red-700 px-2">Remove</button>
              </div>
              <p *ngIf="deskPairs.length === 0" class="text-xs text-gray-400">
                No shared desks yet — add a pair above.
              </p>
              <p *ngIf="exhibitorOptions.length === 0" class="text-xs text-amber-600">
                Exhibitor list unavailable — pairs still save by id, but names won't show.
              </p>
            </div>

            <div class="flex items-center gap-3 pt-3">
              <button (click)="saveDeskPairs()" class="ec-btn">Save shared desks</button>
              <span *ngIf="deskPairsMessage" class="text-xs text-red-500">{{ deskPairsMessage }}</span>
            </div>
          </section>
        </div>
      </mat-tab>

      <!-- Logistics -->
      <mat-tab label="Logistics">
        <div class="p-6 space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Hotel</span>
              <input [(ngModel)]="logistics.hotel" class="ec-input" placeholder="Crowne Plaza Marseille ****" />
              <span class="ec-hint">Hotel name (and stars) shown in the account info / PDF. e.g. “Crowne Plaza Marseille ****”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Event space</span>
              <input [(ngModel)]="logistics.event_space" class="ec-input" placeholder="Salle de Workshop, Crowne Plaza" />
              <span class="ec-hint">Where the workshop takes place. e.g. “Salle de Workshop, Crowne Plaza”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Event date</span>
              <input [(ngModel)]="logistics.event_date" class="ec-input" placeholder="24.–27.09.2026" />
              <span class="ec-hint">Human-readable event date range. e.g. “24.–27.09.2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Book night by</span>
              <input [(ngModel)]="logistics.book_night" class="ec-input" placeholder="24.09 / 25.09 / 26.09 2026" />
              <span class="ec-hint">Which nights participants can book. e.g. “24.09 / 25.09 / 26.09 2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Cancel date</span>
              <input [(ngModel)]="logistics.cancel_date" class="ec-input" placeholder="Sep 1st 2026" />
              <span class="ec-hint">Free-cancellation deadline. e.g. “Sep 1st 2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Day 1 label</span>
              <input [(ngModel)]="logistics.day1" class="ec-input" placeholder="25.09.2026" />
              <span class="ec-hint">Label for the first meeting day. e.g. “25.09.2026”.</span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Day 2 label</span>
              <input [(ngModel)]="logistics.day2" class="ec-input" placeholder="26.09.2026" />
              <span class="ec-hint">Label for the second meeting day. e.g. “26.09.2026”.</span>
            </label>
          </div>
          <div class="pt-2">
            <button (click)="saveLogistics()" class="ec-btn">Save logistics</button>
          </div>

          <!-- Registration form dates (frontend client JSON) -->
          <div class="mt-8 border-t border-gray-200 pt-6" *ngIf="frontendDatesLoaded; else frontendDatesMissing">
            <h3 class="text-sm font-bold text-gray-900">Registration form dates &amp; labels</h3>
            <p class="text-xs text-gray-500 max-w-2xl mb-3">
              The dates and labels printed on the public registration &amp; profile forms. Stored in the
              frontend client config (<code>assets/clients/&lt;client&gt;.json</code>) and also applied to the
              legacy pages via the config bridge. Changes take effect on the next reload of the public app.
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Day 1 (form label)</span>
                <input [(ngModel)]="frontendDates.day_1" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="01.10.2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Day 2 (form label)</span>
                <input [(ngModel)]="frontendDates.day_2" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="02.10.2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Event dates (range text)</span>
                <input [(ngModel)]="frontendDates.event_dates" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="01.–02.10.2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Event date</span>
                <input [(ngModel)]="frontendDates.event_date" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="01.10.2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Book night</span>
                <input [(ngModel)]="frontendDates.book_night" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="01.10./02.10.2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Cancel date</span>
                <input [(ngModel)]="frontendDates.cancel_date" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="September 1st 2026" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Arrival day-before date</span>
                <input [(ngModel)]="frontendDates.arrival_day_before_date" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="29.09.2026" />
              </label>
              <label class="block sm:col-span-2">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Lunch dates (comma-separated)</span>
                <input [(ngModel)]="frontendDates.lunch_dates" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="02.10.2026" />
                <span class="ec-hint">One lunch checkbox per date on the forms. Separate multiple dates with commas.</span>
              </label>
              <label class="block sm:col-span-2">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Inforeisen trips (comma-separated)</span>
                <input
                  [(ngModel)]="frontendDates.inforeisen_trips"
                  (ngModelChange)="syncInforeisenPdfSlots()"
                  [ngModelOptions]="{ standalone: true }"
                  [readonly]="frontendDatesReadonly"
                  class="ec-input"
                  placeholder="Trip A, Trip B, Trip C" />
                <span class="ec-hint">Visitor ranked-choice options (1st / 2nd / 3rd choice).</span>
              </label>

              <div class="sm:col-span-2" *ngIf="inforeisenTripNames().length">
                <h4 class="text-sm font-bold text-gray-900 mb-2">Inforeisen PDFs</h4>
                <p class="text-xs text-gray-500 max-w-2xl mb-3">
                  One PDF per trip; shown as links on the visitor Inforeisen step (caption = trip name, opens in a new window).
                  Uploads are saved automatically. Trip captions come from the <strong>Inforeisen trips</strong> field above.
                  Reload the visitor registration after changes.
                </p>
                <div class="space-y-4">
                  <div
                    *ngFor="let trip of inforeisenTripNames(); let i = index"
                    class="border border-gray-200 rounded-lg p-4">
                    <div class="text-sm font-medium text-gray-900 mb-2">{{ trip }}</div>
                    <div *ngIf="inforeisen_pdfs[i]" class="mb-2 text-xs">
                      Current:
                      <a
                        [href]="inforeisePdfUrl(inforeisen_pdfs[i])"
                        target="_blank"
                        rel="noopener"
                        class="text-blue-600 underline hover:text-blue-800 ml-1">
                        {{ inforeisen_pdfs[i] }}
                      </a>
                      <button
                        type="button"
                        *ngIf="!frontendDatesReadonly"
                        (click)="clearInforeisenPdf(i)"
                        class="ml-3 text-xs text-red-600 underline">
                        Remove
                      </button>
                    </div>
                    <file-pond
                      *ngIf="!frontendDatesReadonly"
                      [options]="pondInforeisePdfOptions"
                      (onprocessfile)="onInforeisePdfUploaded(i, $event)"></file-pond>
                  </div>
                </div>
              </div>

              <label class="block sm:col-span-2">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Nîmes visit label (exhibitor)</span>
                <input [(ngModel)]="frontendDates.nimes_visit_label" [ngModelOptions]="{ standalone: true }" [readonly]="frontendDatesReadonly" class="ec-input" placeholder="Je participe à une visite guidée de Nîmes…" />
              </label>
            </div>
            <div class="pt-3 flex items-center gap-3">
              <button (click)="saveFrontendDates()" class="ec-btn" [disabled]="frontendDatesReadonly">Save registration dates</button>
              <span *ngIf="frontendDatesReadonly" class="text-xs text-amber-600">Frontend client file is read-only on this deployment.</span>
              <span *ngIf="frontendDatesMessage" class="text-xs text-red-600">{{ frontendDatesMessage }}</span>
            </div>
          </div>
          <ng-template #frontendDatesMissing>
            <div class="mt-8 border-t border-gray-200 pt-6">
              <p class="text-xs text-gray-400 max-w-2xl">
                Registration form dates: the frontend client config is not reachable from the API on this
                deployment. Edit <code>assets/clients/&lt;client&gt;.json</code> directly, or set
                <code>CONFIG_WEBAPP_ASSETS_DIR</code> so the API can write it.
              </p>
            </div>
          </ng-template>
        </div>
      </mat-tab>

      <!-- Media -->
      <mat-tab label="Media">
        <div class="p-6 space-y-6">

          <p class="text-xs text-gray-500 max-w-2xl">
            Upload brand assets here. Images: JPG, PNG or WebP. The hero video plays on the public front page —
            provide both MP4 and WebM for broad browser support.
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Brand / event logo</span>
              <span class="ec-hint">Logo above the welcome heading on the front page. PNG recommended (transparent or solid background). Upload saves automatically — reload the public app after uploading.</span>
              <file-pond [options]="pondImageOptions" (onprocessfile)="onMediaUploaded('brand_logo', $event)"></file-pond>
              <img *ngIf="media.brand_logo" [src]="mediaUrl(media.brand_logo)" class="mt-2 h-16 w-auto rounded" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Navigation background</span>
              <span class="ec-hint">Wide banner behind the top navigation. Landscape JPG, ~1920×400px.</span>
              <file-pond [options]="pondImageOptions" (onprocessfile)="onMediaUploaded('nav_background', $event)"></file-pond>
              <img *ngIf="media.nav_background" [src]="mediaUrl(media.nav_background)" class="mt-2 h-16 w-auto rounded" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Email logo</span>
              <span class="ec-hint">Logo embedded at the top of outgoing emails. PNG on white, ~300px wide.</span>
              <file-pond [options]="pondImageOptions" (onprocessfile)="onMediaUploaded('email_logo', $event)"></file-pond>
              <img *ngIf="media.email_logo" [src]="mediaUrl(media.email_logo)" class="mt-2 h-16 w-auto rounded" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">PDF logo</span>
              <span class="ec-hint">Logo on generated PDFs (agenda, account info). PNG/JPG, ~300px wide.</span>
              <file-pond [options]="pondImageOptions" (onprocessfile)="onMediaUploaded('pdf_logo', $event)"></file-pond>
              <img *ngIf="media.pdf_logo" [src]="mediaUrl(media.pdf_logo)" class="mt-2 h-16 w-auto rounded" />
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">PDF footer image</span>
              <span class="ec-hint">Footer strip on PDFs (sponsors/partners). Wide JPG/PNG.</span>
              <file-pond [options]="pondImageOptions" (onprocessfile)="onMediaUploaded('pdf_footer', $event)"></file-pond>
              <img *ngIf="media.pdf_footer" [src]="mediaUrl(media.pdf_footer)" class="mt-2 h-16 w-auto rounded" />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Front-page hero video (MP4)</span>
              <span class="ec-hint">Background video for the landing page. H.264 MP4, 1080p, ideally &lt; 30s loop.</span>
              <file-pond [options]="pondVideoOptions" (onprocessfile)="onMediaUploaded('hero_video_mp4', $event)"></file-pond>
              <p *ngIf="media.hero_video_mp4" class="mt-1 text-xs text-gray-400 break-all">{{ media.hero_video_mp4 }}</p>
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Front-page hero video (WebM)</span>
              <span class="ec-hint">Same clip as WebM for Firefox/Chrome fallback.</span>
              <file-pond [options]="pondVideoOptions" (onprocessfile)="onMediaUploaded('hero_video_webm', $event)"></file-pond>
              <p *ngIf="media.hero_video_webm" class="mt-1 text-xs text-gray-400 break-all">{{ media.hero_video_webm }}</p>
            </div>
          </div>

          <div class="pt-2">
            <button (click)="saveMedia()" class="ec-btn">Save media</button>
          </div>
        </div>
      </mat-tab>

      <!-- Gallery (Phase 6) — hero + intro + albums (categories) + batch upload -->
      <mat-tab label="Gallery">
        <ng-template matTabContent>
          <div class="p-6 space-y-8">
            <p class="text-xs text-gray-500 max-w-2xl">
              Build the visitor photo gallery: a main hero image and intro text, plus one or more albums
              (categories). Upload photos in batches per album (hundreds supported, JPG/PNG/WebP). Titles,
              cover images and order are configurable and the public gallery renders dynamically from these.
            </p>

            <!-- Main hero + intro -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Main hero image</span>
                <span class="ec-hint">Large banner on the gallery landing page. Wide JPG/PNG, ~1920×640px.</span>
                <file-pond [options]="pondGalleryHeroOptions" (onprocessfile)="onGalleryHeroUploaded($event)"></file-pond>
                <div *ngIf="galleryMeta.hero" class="mt-2 relative inline-block">
                  <img [src]="galleryPathUrl(galleryMeta.hero)" class="h-24 w-auto rounded border border-gray-200" />
                  <button
                    (click)="galleryMeta.hero = ''"
                    title="Remove hero"
                    class="absolute top-1 right-1 bg-white/90 text-red-600 rounded px-1.5 text-sm leading-none"
                  >&times;</button>
                </div>
              </div>
              <div>
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Intro text (HTML allowed)</span>
                <span class="ec-hint">Shown above the album cards, e.g. a thank-you message.</span>
                <textarea
                  class="ec-input"
                  rows="5"
                  [(ngModel)]="galleryMeta.intro"
                  [ngModelOptions]="{ standalone: true }"
                  placeholder="&lt;p&gt;We thank you all for attending…&lt;/p&gt;"
                ></textarea>
              </div>
            </div>

            <!-- Albums (categories) editor -->
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-base font-bold text-gray-900">Albums</span>
                <div class="flex items-end gap-2">
                  <div>
                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">New / select album</span>
                    <input
                      class="ec-input"
                      list="ec-gallery-cats"
                      [(ngModel)]="galleryCategory"
                      [ngModelOptions]="{ standalone: true }"
                      placeholder="e.g. marseille"
                    />
                    <datalist id="ec-gallery-cats">
                      <option *ngFor="let c of galleryCategories" [value]="c"></option>
                    </datalist>
                  </div>
                  <button (click)="addGalleryCategory()" class="ec-btn-secondary mb-px">Add album</button>
                  <button (click)="loadGallery()" class="ec-btn-secondary mb-px">Refresh</button>
                </div>
              </div>

              <p *ngIf="galleryMeta.categories.length === 0" class="text-xs text-gray-400">
                No albums yet. Type a name above and click “Add album”, then upload images below.
              </p>

              <div
                *ngFor="let cat of galleryMeta.categories; let i = index; trackBy: trackByIndex"
                class="border rounded-lg p-4"
                [class.border-blue-400]="cat.slug === galleryCategory"
                [class.border-gray-200]="cat.slug !== galleryCategory"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-mono px-2 py-1 bg-gray-100 rounded">{{ cat.slug }}</span>
                    <span class="text-xs text-gray-400">{{ imagesForCategory(cat.slug).length }} images</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <button (click)="moveGalleryCategory(i, -1)" [disabled]="i === 0" title="Move up" class="ec-btn-secondary px-2">↑</button>
                    <button (click)="moveGalleryCategory(i, 1)" [disabled]="i === galleryMeta.categories.length - 1" title="Move down" class="ec-btn-secondary px-2">↓</button>
                    <button (click)="selectGalleryCategory(cat.slug)" title="Upload to this album" class="ec-btn-secondary px-2">Select</button>
                    <button (click)="removeGalleryCategory(cat)" title="Remove album" class="text-red-600 px-2">Remove</button>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div>
                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</span>
                    <input class="ec-input" [(ngModel)]="cat.title" [ngModelOptions]="{ standalone: true }" placeholder="Images Workshop" />
                  </div>
                  <div>
                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</span>
                    <input class="ec-input" [(ngModel)]="cat.description" [ngModelOptions]="{ standalone: true }" placeholder="Click on an image to enlarge." />
                  </div>
                  <div>
                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Caption / credit</span>
                    <input class="ec-input" [(ngModel)]="cat.caption" [ngModelOptions]="{ standalone: true }" placeholder="Images © Photographer" />
                  </div>
                </div>

                <div class="mt-3">
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Cover image</span>
                  <span class="ec-hint">Click an image to view it large; use the ★ to set it as this album’s cover. Empty = newest image.</span>
                  <p *ngIf="imagesForCategory(cat.slug).length === 0" class="text-xs text-gray-400">
                    No images yet — Select this album and upload below.
                  </p>
                  <div class="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mt-1">
                    <div
                      *ngFor="let img of imagesForCategory(cat.slug); let imgIdx = index; trackBy: trackByIndex"
                      class="relative group border rounded overflow-hidden"
                      [class.ring-2]="cat.cover === img.path"
                      [class.ring-blue-500]="cat.cover === img.path"
                      [class.border-gray-200]="cat.cover !== img.path"
                    >
                      <img
                        [src]="galleryUrl(img)"
                        [alt]="img.name"
                        class="w-full h-16 object-cover bg-gray-50 cursor-zoom-in"
                        loading="lazy"
                        (click)="openLightbox(imagesForCategory(cat.slug), imgIdx)"
                      />
                      <div class="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100">
                        <button
                          (click)="setGalleryCover(cat, img); $event.stopPropagation()"
                          [title]="cat.cover === img.path ? 'Cover image' : 'Set as cover'"
                          class="bg-white/90 rounded px-1 text-xs leading-none"
                          [class.text-blue-600]="cat.cover === img.path"
                          [class.text-gray-400]="cat.cover !== img.path"
                        >&#9733;</button>
                        <button
                          (click)="deleteGalleryImage(img); $event.stopPropagation()"
                          title="Delete image"
                          class="bg-white/90 text-red-600 rounded px-1 text-xs leading-none"
                        >&times;</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Batch upload into the selected album -->
            <div class="space-y-2 border-t pt-5">
              <span class="text-base font-bold text-gray-900">
                Upload images into “{{ galleryCategory }}”
              </span>
              <p class="ec-hint">Photos are added to the album selected above. Type a new name and “Add album” first to create one.</p>
              <file-pond [options]="pondGalleryOptions" (onprocessfile)="onGalleryUploaded()"></file-pond>
            </div>

            <div class="pt-2">
              <button (click)="saveGalleryMeta()" class="ec-btn">Save gallery</button>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Operations (Stage 5A) — first of the right-aligned settings cluster -->
      <mat-tab label="Operations" labelClass="ec-tab-right">
        <div class="p-6 space-y-8">

          <!-- Operational flags -->
          <section>
            <h3 class="text-base font-bold text-gray-900">Operational flags</h3>
            <p class="ec-hint mb-2">Master switches for the event front-end.</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" [(ngModel)]="flags.booking_stop" />
                Booking stop (appointments closed)
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" [(ngModel)]="flags.show_gallery" />
                Show photo gallery
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" [(ngModel)]="flags.exhibitor_booking_active" />
                Exhibitor booking active
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" [(ngModel)]="flags.test_version" />
                Test version (non-production banner)
              </label>
            </div>
          </section>

          <!-- Email notification toggles -->
          <section>
            <h3 class="text-base font-bold text-gray-900">Email notifications</h3>
            <p class="ec-hint mb-2">Which automatic emails the system sends.</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label *ngFor="let n of notificationLabels" class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" [(ngModel)]="notifications[n.key]" [ngModelOptions]="{ standalone: true }" />
                {{ n.label }}
              </label>
            </div>
          </section>

          <!-- Test recipients -->
          <section>
            <h3 class="text-base font-bold text-gray-900">Test recipients</h3>
            <p class="ec-hint mb-2">Addresses used when the test version is active, instead of real participants.</p>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Visitor</span>
                <input [(ngModel)]="testmail.visitor" class="ec-input" placeholder="visitor-test@example.com" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Exhibitor</span>
                <input [(ngModel)]="testmail.exhibitor" class="ec-input" placeholder="exhibitor-test@example.com" />
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Organiser (Atout)</span>
                <input [(ngModel)]="testmail.atout" class="ec-input" placeholder="organiser-test@example.com" />
              </label>
            </div>
          </section>

          <!-- Mail subject + deadline texts -->
          <section>
            <h3 class="text-base font-bold text-gray-900">Mail subject &amp; deadlines</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label class="block sm:col-span-2">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Email subject prefix</span>
                <input [(ngModel)]="event.mail_subject" class="ec-input" placeholder="Bonjour France 2026" />
                <span class="ec-hint">Subject line / prefix used on outgoing event emails. e.g. “Bonjour France 2026”.</span>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Complete profile by</span>
                <input [(ngModel)]="event.complete_profile_by" class="ec-input" placeholder="15.09.2026" />
                <span class="ec-hint">Deadline shown in “complete your profile by [date]” messages.</span>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Validate registration latest</span>
                <input [(ngModel)]="event.validate_registration_latest" class="ec-input" placeholder="20.09.2026" />
                <span class="ec-hint">Latest date to validate a registration, used in reminder texts.</span>
              </label>
            </div>
          </section>

          <div class="pt-2">
            <button (click)="saveOperations()" class="ec-btn">Save operations</button>
          </div>
        </div>
      </mat-tab>

      <!-- Email Templates (Phase 3) -->
      <mat-tab label="E-mail">
        <ng-template matTabContent>
          <div class="p-6 space-y-6">
            <p class="text-xs text-gray-400">
              Transactional emails, editable per active language. Pick an email below, then edit its
              subject and body. Use the
              <code class="ec-token">{{ tokenExample }}</code> placeholders listed under each
              email; they are filled in automatically when the mail is sent. Leaving a template
              empty keeps the system's built-in design.
            </p>

            <!-- One sub-tab per email type; navigated via the dropdown. -->
            <div class="ec-subnav">
              <label class="ec-subnav-label">Email</label>
              <select
                class="ec-input ec-subnav-select"
                [(ngModel)]="emailTabIndex"
                [ngModelOptions]="{ standalone: true }"
              >
                <option *ngFor="let block of emailBlocks; let i = index" [ngValue]="i">
                  {{ block.label }}
                </option>
              </select>
            </div>
            <mat-tab-group
              class="ec-subtabs ec-subtabs--dropdown"
              mat-stretch-tabs="false"
              animationDuration="0ms"
              [(selectedIndex)]="emailTabIndex"
            >
              <mat-tab *ngFor="let block of emailBlocks" [label]="block.label">
                <ng-template matTabContent>
                  <div class="pt-5 space-y-5">
                    <h3 class="text-base font-bold text-gray-900">
                      {{ block.label }}
                      <span class="text-gray-400 font-normal">({{ block.key }})</span>
                    </h3>
                    <p class="text-xs text-gray-400">
                      Available tokens:
                      <code *ngFor="let t of block.tokens" class="ec-token">{{ token(t) }}</code>
                    </p>

                    <div *ngFor="let lang of emailLangs(block)">
                      <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">{{ langLabel(lang) }} ({{ lang }})</span>
                      <input
                        [(ngModel)]="emailModel(block.key, lang).subject"
                        [ngModelOptions]="{ standalone: true }"
                        placeholder="Email subject — e.g. {{ subjectExample }}"
                        class="ec-input mb-2"
                      />
                      <editor
                        [init]="editorInit"
                        [(ngModel)]="emailModel(block.key, lang).body_html"
                        [ngModelOptions]="{ standalone: true }"
                      ></editor>
                    </div>
                  </div>
                </ng-template>
              </mat-tab>
            </mat-tab-group>

            <div class="pt-2">
              <button (click)="saveEmails()" class="ec-btn">Save email templates</button>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- System -->
      <mat-tab label="System">
        <div class="p-6 space-y-4">
          <!-- Main language per role is the single source of truth: it drives
               which one language each content page is authored/served in AND the
               deduped set of active languages used by Email, the Languages/i18n
               editor and the Lists. -->
          <div class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-4">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Main language per role</span>
            <p class="mt-1 text-xs text-gray-400 max-w-2xl">
              Each content page is authored and shown in one language based on its role: visitor pages use the
              visitor language, exhibitor pages the exhibitor language. System is the default/fallback (e.g. the
              public login page). These three languages also drive the Email, Languages/i18n and Lists editors.
            </p>
            <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">System / default</span>
                <select class="ec-input" [(ngModel)]="roleLanguages.system" [ngModelOptions]="{ standalone: true }">
                  <option *ngFor="let lang of availableLanguages" [value]="lang.code">{{ lang.label }} ({{ lang.code }})</option>
                </select>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Visitor</span>
                <select class="ec-input" [(ngModel)]="roleLanguages.visitor" [ngModelOptions]="{ standalone: true }">
                  <option *ngFor="let lang of availableLanguages" [value]="lang.code">{{ lang.label }} ({{ lang.code }})</option>
                </select>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Exhibitor</span>
                <select class="ec-input" [(ngModel)]="roleLanguages.exhibitor" [ngModelOptions]="{ standalone: true }">
                  <option *ngFor="let lang of availableLanguages" [value]="lang.code">{{ lang.label }} ({{ lang.code }})</option>
                </select>
              </label>
            </div>

            <dl class="mt-4 space-y-3 text-sm border-t border-gray-200 pt-3">
              <div class="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1">
                <dt class="font-medium text-gray-700">Admin back-office</dt>
                <dd class="text-gray-600">
                  {{ adminUiLanguage }}
                  <span class="block text-xs text-gray-400">Fixed (Angular superadmin + eplus pages); the legacy CMS is German.</span>
                </dd>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1">
                <dt class="font-medium text-gray-700">Event country</dt>
                <dd class="text-gray-600">
                  {{ eventCountryCode || 'not set' }}
                  <span class="block text-xs text-gray-400">Registration routing (veranstaltungs_land), not a UI language.</span>
                </dd>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1">
                <dt class="font-medium text-gray-700">Guided-tour preference</dt>
                <dd class="text-gray-600">
                  per visitor / exhibitor
                  <span class="block text-xs text-gray-400">Each person picks accepted tour languages on their profile (langulage_guide, 1-7); not a UI or email language.</span>
                </dd>
              </div>
            </dl>
          </div>

          <div class="pt-2">
            <button (click)="saveSystem()" class="ec-btn">Save system</button>
          </div>
        </div>
      </mat-tab>

      <!-- Languages (Phase 7) — per-language UI wording (event_i18n) -->
      <mat-tab label="Languages">
        <ng-template matTabContent>
          <div class="p-6 space-y-4">
            <p class="text-xs text-gray-500 max-w-2xl">
              Per-language UI wording. Each row is a key plus its text in every active language. Legacy pages opt
              in with <code class="ec-token">lc_t('key')</code> (or the short <code class="ec-token">t('key')</code>);
              empty values are not stored, so the page falls back to its built-in wording.
            </p>

            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="text-left text-gray-500">
                    <th class="py-2 pr-3 font-medium">Key</th>
                    <th *ngFor="let lang of languages" class="py-2 pr-3 font-medium">
                      {{ langLabel(lang) }} ({{ lang }})
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of i18nRows; let i = index" class="border-t border-gray-100">
                    <td class="py-1 pr-3 align-top">
                      <input
                        class="ec-input"
                        [(ngModel)]="row.key"
                        [ngModelOptions]="{ standalone: true }"
                        placeholder="key_name"
                      />
                    </td>
                    <td *ngFor="let lang of languages" class="py-1 pr-3 align-top">
                      <input
                        class="ec-input"
                        [ngModel]="i18nValue(row, lang)"
                        (ngModelChange)="setI18nValue(row, lang, $event)"
                        [ngModelOptions]="{ standalone: true }"
                      />
                    </td>
                    <td class="py-1 align-top">
                      <button (click)="removeI18nRow(i)" class="text-xs text-red-500 hover:text-red-700 px-2">
                        Remove
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="flex items-center gap-3 pt-2">
              <button (click)="addI18nRow()" class="ec-btn-secondary">Add key</button>
              <button (click)="saveI18n()" class="ec-btn">Save languages</button>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Lists (Stage 5B) — per-language activities + flat categories -->
      <mat-tab label="Lists">
        <ng-template matTabContent>
          <div class="p-6 space-y-8">

            <!-- Activities: per active language, aligned by row -->
            <section>
              <div class="flex items-center justify-between mb-2">
                <div>
                  <h3 class="text-base font-bold text-gray-900">Activities</h3>
                  <p class="ec-hint">
                    Translatable list shown to participants by language. Each row is the same option in
                    every language — keep translations on the same line.
                  </p>
                </div>
                <button (click)="addActivityRow()" class="ec-btn-secondary">Add activity</button>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th class="w-8 py-1">#</th>
                      <th *ngFor="let lang of languages" class="py-1 px-2">{{ langLabel(lang) }} ({{ lang }})</th>
                      <th class="w-24 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let i of activityRows" class="border-t border-gray-100">
                      <td class="py-1 text-xs text-gray-400 align-middle">{{ i + 1 }}</td>
                      <td *ngFor="let lang of languages" class="py-1 px-2">
                        <input
                          [(ngModel)]="activitiesByLang[lang][i]"
                          [ngModelOptions]="{ standalone: true }"
                          class="ec-input"
                          [placeholder]="lang === 'fr' ? 'Hébergement' : (lang === 'de' ? 'Unterkunft' : 'Accomodation')"
                        />
                      </td>
                      <td class="py-1 align-middle whitespace-nowrap">
                        <button (click)="moveActivityRow(i, -1)" class="ec-btn-secondary px-2" title="Move up">↑</button>
                        <button (click)="moveActivityRow(i, 1)" class="ec-btn-secondary px-2" title="Move down">↓</button>
                        <button (click)="removeActivityRow(i)" class="text-xs text-red-500 hover:text-red-700 px-2">Remove</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p *ngIf="activityRowCount === 0" class="text-xs text-gray-400">No activities yet — add one above.</p>
              </div>
            </section>

            <!-- Exhibitor categories: single language-neutral list -->
            <section>
              <div class="flex items-center justify-between mb-2">
                <div>
                  <h3 class="text-base font-bold text-gray-900">Exhibitor categories</h3>
                  <p class="ec-hint">
                    Single list (not translated): these values are used as stored category keys for
                    exhibitors, so keep them stable.
                  </p>
                </div>
                <button (click)="addCategory()" class="ec-btn-secondary">Add category</button>
              </div>

              <div class="space-y-2 max-w-xl">
                <div *ngFor="let c of categories; let i = index; trackBy: trackByIndex" class="flex items-center gap-2">
                  <span class="w-6 text-xs text-gray-400">{{ i + 1 }}</span>
                  <input
                    [(ngModel)]="categories[i]"
                    [ngModelOptions]="{ standalone: true }"
                    class="ec-input flex-1"
                    placeholder="Accommodation"
                  />
                  <button (click)="moveCategory(i, -1)" class="ec-btn-secondary px-2" title="Move up">↑</button>
                  <button (click)="moveCategory(i, 1)" class="ec-btn-secondary px-2" title="Move down">↓</button>
                  <button (click)="removeCategory(i)" class="text-xs text-red-500 hover:text-red-700 px-2">Remove</button>
                </div>
                <p *ngIf="categories.length === 0" class="text-xs text-gray-400">No categories yet — add one above.</p>
              </div>
            </section>

            <div class="pt-2">
              <button (click)="saveLists()" class="ec-btn">Save lists</button>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Config files (Stage 5D) — raw editor for allowlisted JSON files -->
      <mat-tab label="Config files">
        <ng-template matTabContent>
          <div class="p-6">
            <p class="text-xs text-gray-400 mb-4 max-w-2xl">
              Edit configuration files directly. Only <code>.json</code> files are editable here;
              credential files (client JSON, <code>.env</code>) and PHP are shown read-only and must
              be edited on the server. Every save validates the JSON and keeps a timestamped backup.
            </p>

            <div class="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-4">
              <!-- file list -->
              <div class="rounded-lg border border-gray-200 overflow-hidden">
                <p
                  *ngIf="writableConfigFiles.length"
                  class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50 border-b border-gray-100"
                >Editable</p>
                <button
                  *ngFor="let f of writableConfigFiles"
                  type="button"
                  (click)="selectConfigFile(f.id)"
                  class="w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 focus:outline-none"
                  [class.bg-blue-50]="f.id === selectedFileId"
                >
                  <span class="block font-medium text-gray-700 truncate">{{ f.label }}</span>
                  <span class="block text-[11px] text-gray-400">{{ f.type }}</span>
                </button>

                <p
                  *ngIf="readonlyConfigFiles.length"
                  class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50 border-b border-gray-100"
                >View-only (secrets / server)</p>
                <button
                  *ngFor="let f of readonlyConfigFiles"
                  type="button"
                  (click)="selectConfigFile(f.id)"
                  class="w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 focus:outline-none"
                  [class.bg-blue-50]="f.id === selectedFileId"
                >
                  <span class="block font-medium text-gray-700 truncate">{{ f.label }}</span>
                  <span class="block text-[11px] text-gray-400">
                    {{ f.type }} · read-only
                  </span>
                </button>

                <p *ngIf="configFiles.length === 0" class="px-3 py-2 text-xs text-gray-400">
                  No config files found.
                </p>
              </div>

              <!-- editor -->
              <div *ngIf="selectedFileId">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {{ selectedFileId }}
                    <span *ngIf="fileReadonly" class="ml-2 text-amber-600 normal-case">(read-only)</span>
                  </span>
                  <div class="flex items-center gap-3">
                    <span *ngIf="fileMessage" class="text-xs text-emerald-600">{{ fileMessage }}</span>
                    <div class="flex items-center gap-1">
                      <span class="text-[11px] text-gray-400 mr-1">Font</span>
                      <button
                        type="button"
                        (click)="decFileFont()"
                        title="Smaller text"
                        class="px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >A&minus;</button>
                      <button
                        type="button"
                        (click)="incFileFont()"
                        title="Larger text"
                        class="px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >A+</button>
                    </div>
                  </div>
                </div>
                <textarea
                  [(ngModel)]="fileContent"
                  (ngModelChange)="validateFileJson()"
                  [readonly]="fileReadonly"
                  rows="22"
                  spellcheck="false"
                  class="ec-input font-mono"
                  [style.fontSize.px]="fileFontSize"
                  [style.lineHeight.px]="fileFontSize + 6"
                  [class.bg-gray-50]="fileReadonly"
                ></textarea>
                <p *ngIf="fileError" class="mt-1 text-xs text-red-600">{{ fileError }}</p>
                <div class="pt-3" *ngIf="!fileReadonly">
                  <button (click)="saveConfigFile()" class="ec-btn" [disabled]="!!fileError">Save file</button>
                </div>

                <!-- Per-file documentation + example -->
                <div *ngIf="currentFileHelp" class="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 class="text-sm font-semibold text-gray-700">{{ currentFileHelp.title }} — how it works</h4>
                  <p class="mt-1 text-xs text-gray-500 leading-relaxed max-w-2xl">{{ currentFileHelp.description }}</p>
                  <p class="mt-3 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Example</p>
                  <pre class="mt-1 overflow-x-auto rounded bg-white border border-gray-200 p-3 text-[11px] leading-relaxed text-gray-700">{{ currentFileHelp.example }}</pre>
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Admins: superuser-only backend admin user management -->
      <mat-tab label="Admins" labelClass="ec-tab-right" *ngIf="isSuperuser">
        <ng-template matTabContent>
          <div class="p-6 space-y-6">
            <div class="flex items-start justify-between gap-4">
              <p class="text-xs text-gray-500 max-w-2xl">
                Manage backend <strong>admin</strong> users (full back-office access). Create new admins,
                deactivate or reactivate existing ones, and set a profile image. Superuser accounts are
                listed for reference but cannot be edited or deactivated here.
              </p>
              <button (click)="newAdminUser()" class="ec-btn shrink-0">New admin</button>
            </div>

            <p *ngIf="adminMessage" class="text-xs text-emerald-600">{{ adminMessage }}</p>

            <!-- list -->
            <div class="rounded-lg border border-gray-200 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th class="text-left font-medium px-3 py-2">Admin</th>
                    <th class="text-left font-medium px-3 py-2">Login</th>
                    <th class="text-left font-medium px-3 py-2">Email</th>
                    <th class="text-left font-medium px-3 py-2">Status</th>
                    <th class="text-left font-medium px-3 py-2">Last login</th>
                    <th class="text-right font-medium px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let u of adminUsers" class="border-t border-gray-100">
                    <td class="px-3 py-2">
                      <div class="flex items-center gap-2">
                        <img
                          *ngIf="adminAvatarUrl(u)"
                          [src]="adminAvatarUrl(u)"
                          class="h-8 w-8 rounded-full object-cover border border-gray-200"
                        />
                        <span
                          *ngIf="!adminAvatarUrl(u)"
                          class="h-8 w-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[11px] text-gray-400"
                        >{{ (u.name || u.login || '?').charAt(0) }}</span>
                        <span class="font-medium text-gray-700">
                          {{ u.name || '—' }}
                          <span
                            *ngIf="u.su === 1"
                            class="ml-1 inline-block rounded bg-purple-100 text-purple-700 px-1.5 py-0.5 text-[10px] align-middle"
                          >Superuser</span>
                          <span *ngIf="u.company" class="block text-[11px] text-gray-400">{{ u.company }}</span>
                        </span>
                      </div>
                    </td>
                    <td class="px-3 py-2 text-gray-600">{{ u.login }}</td>
                    <td class="px-3 py-2 text-gray-600">{{ u.email || '—' }}</td>
                    <td class="px-3 py-2">
                      <span
                        *ngIf="u.active === 1"
                        class="inline-block rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[11px]"
                      >Active</span>
                      <span
                        *ngIf="u.active !== 1"
                        class="inline-block rounded bg-gray-200 text-gray-600 px-1.5 py-0.5 text-[11px]"
                      >Inactive</span>
                    </td>
                    <td class="px-3 py-2 text-[11px] text-gray-400">{{ u.lastlogin || '—' }}</td>
                    <td class="px-3 py-2">
                      <div class="flex items-center justify-end gap-2">
                        <button
                          (click)="editAdminUser(u)"
                          [disabled]="u.su === 1"
                          class="ec-btn-secondary"
                          [class.opacity-40]="u.su === 1"
                        >Edit</button>
                        <button
                          *ngIf="u.active === 1"
                          (click)="deactivateAdminUser(u)"
                          [disabled]="u.su === 1"
                          class="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"
                          [class.opacity-40]="u.su === 1"
                        >Deactivate</button>
                        <button
                          *ngIf="u.active !== 1"
                          (click)="reactivateAdminUser(u)"
                          [disabled]="u.su === 1"
                          class="text-xs px-3 py-1.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          [class.opacity-40]="u.su === 1"
                        >Reactivate</button>
                      </div>
                    </td>
                  </tr>
                  <tr *ngIf="adminUsers.length === 0">
                    <td colspan="6" class="px-3 py-4 text-center text-xs text-gray-400">No admin users found.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- create / edit form -->
            <div *ngIf="adminEditing" class="rounded-lg border border-gray-200 bg-gray-50 p-5 space-y-4">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-gray-700">
                  {{ adminForm.id ? 'Edit admin' : 'New admin' }}
                </h4>
                <button (click)="cancelAdminEdit()" class="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</span>
                  <input class="ec-input" [(ngModel)]="adminForm.name" [ngModelOptions]="{ standalone: true }" />
                </div>
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Login</span>
                  <input
                    class="ec-input"
                    [(ngModel)]="adminForm.login"
                    [ngModelOptions]="{ standalone: true }"
                    [readonly]="!!adminForm.id"
                    [class.bg-gray-100]="!!adminForm.id"
                  />
                  <span *ngIf="adminForm.id" class="ec-hint">The login cannot be changed after creation.</span>
                </div>
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</span>
                  <input class="ec-input" [(ngModel)]="adminForm.email" [ngModelOptions]="{ standalone: true }" />
                </div>
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Company</span>
                  <input class="ec-input" [(ngModel)]="adminForm.company" [ngModelOptions]="{ standalone: true }" />
                </div>
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {{ adminForm.id ? 'New password (leave blank to keep)' : 'Password' }}
                  </span>
                  <input
                    type="password"
                    class="ec-input"
                    [(ngModel)]="adminForm.password"
                    [ngModelOptions]="{ standalone: true }"
                    autocomplete="new-password"
                  />
                  <span class="ec-hint">At least 5 characters. Stored as md5 (legacy eplus backend).</span>
                </div>
              </div>

              <!-- avatar uploader: only available once the admin row exists -->
              <div *ngIf="adminFormIsExisting">
                <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Profile image</span>
                <span class="ec-hint">Square JPG/PNG/WebP works best. Uploads immediately.</span>
                <file-pond [options]="pondAdminAvatarOptions"></file-pond>
              </div>
              <p *ngIf="!adminFormIsExisting" class="ec-hint">
                Save the admin first, then a profile image can be uploaded.
              </p>

              <p *ngIf="adminError" class="text-xs text-red-600">{{ adminError }}</p>

              <div class="flex items-center gap-2 pt-1">
                <button (click)="saveAdminUser()" class="ec-btn">
                  {{ adminForm.id ? 'Save admin' : 'Create admin' }}
                </button>
                <button (click)="cancelAdminEdit()" class="ec-btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Sysadmin: read-only system information + manual DB migration -->
      <mat-tab label="Sysadmin" labelClass="ec-tab-right" *ngIf="isSuperuser">
        <div class="p-6 space-y-4">

          <div class="flex items-center justify-between">
            <h3 class="text-base font-bold text-gray-900">System information</h3>
            <button type="button" (click)="loadSysInfo()" class="ec-btn-secondary" [disabled]="sysInfoLoading">
              {{ sysInfoLoading ? 'Refreshing…' : 'Refresh' }}
            </button>
          </div>
          <p class="text-xs text-gray-500 max-w-2xl">
            Read-only overview of the running backend. Secrets (DB / SMTP passwords, JWT keys) are masked and never sent to the browser.
          </p>

          <p *ngIf="sysInfoError" class="text-sm text-red-600">{{ sysInfoError }}</p>

          <!-- Database migration -->
          <div class="rounded-md border border-gray-200 bg-gray-50 p-4">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Database schema</span>
            <dl class="mt-3 grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-4 gap-y-1 text-sm">
              <dt class="font-medium text-gray-700">Current version</dt>
              <dd class="text-gray-600">{{ sysInfo?.schema?.current }}</dd>
              <dt class="font-medium text-gray-700">Target version</dt>
              <dd class="text-gray-600">{{ sysInfo?.schema?.target }}</dd>
              <dt class="font-medium text-gray-700">Pending migrations</dt>
              <dd class="text-gray-600">
                <span *ngIf="sysInfo && sysInfo.schema.pending.length === 0" class="text-emerald-600">none — up to date</span>
                <span *ngIf="sysInfo && sysInfo.schema.pending.length > 0" class="text-amber-600">{{ sysInfo.schema.pending.join(', ') }}</span>
              </dd>
              <dt class="font-medium text-gray-700">t_profile.evening_video</dt>
              <dd class="text-gray-600">
                <span *ngIf="sysInfo && sysInfo.columns['t_profile.evening_video'] === true" class="text-emerald-600">present</span>
                <span *ngIf="sysInfo && sysInfo.columns['t_profile.evening_video'] === false" class="text-amber-600">missing</span>
                <span *ngIf="sysInfo && sysInfo.columns['t_profile.evening_video'] === null" class="text-gray-400">unknown</span>
              </dd>
            </dl>

            <div class="mt-4 flex items-center gap-3">
              <button type="button" (click)="runMigration()" class="ec-btn" [disabled]="migrating">
                {{ migrating ? 'Running…' : 'Run DB migration' }}
              </button>
              <span *ngIf="migrationResult && migrationResult.ok && migrationResult.applied.length > 0" class="text-sm text-emerald-600">
                Applied {{ migrationResult.applied.join(', ') }} (now at version {{ migrationResult.to }}).
              </span>
              <span *ngIf="migrationResult && migrationResult.ok && migrationResult.applied.length === 0" class="text-sm text-gray-500">
                Already up to date (version {{ migrationResult.to }}).
              </span>
            </div>
            <p *ngIf="migrationError" class="mt-2 text-sm text-red-600">{{ migrationError }}</p>
          </div>

          <!-- Runtime -->
          <div class="rounded-md border border-gray-200 bg-gray-50 p-4" *ngIf="sysInfo">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Runtime</span>
            <dl class="mt-3 grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-4 gap-y-1 text-sm">
              <dt class="font-medium text-gray-700">PHP version</dt>
              <dd class="text-gray-600">{{ sysInfo.php.version }} ({{ sysInfo.php.sapi }}, {{ sysInfo.php.os }})</dd>
              <dt class="font-medium text-gray-700">OPcache</dt>
              <dd class="text-gray-600">
                <span [class.text-amber-600]="sysInfo.php.opcache_enabled">{{ sysInfo.php.opcache_enabled ? 'enabled (clear after deploy)' : 'disabled' }}</span>
              </dd>
              <dt class="font-medium text-gray-700">Server time</dt>
              <dd class="text-gray-600">{{ sysInfo.php.server_time }} ({{ sysInfo.php.timezone }})</dd>
              <dt class="font-medium text-gray-700">Active client</dt>
              <dd class="text-gray-600">{{ sysInfo.client.name }} — {{ sysInfo.client.event_title }} {{ sysInfo.client.event_year }}</dd>
            </dl>
          </div>

          <!-- Paths -->
          <div class="rounded-md border border-gray-200 bg-gray-50 p-4" *ngIf="sysInfo">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Locations</span>
            <dl class="mt-3 grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-4 gap-y-1 text-sm break-all">
              <dt class="font-medium text-gray-700">Frontend (this app)</dt>
              <dd class="text-gray-600">{{ env.serverUrl }}</dd>
              <dt class="font-medium text-gray-700">Legacy / eplus base</dt>
              <dd class="text-gray-600">{{ env.eplusSystem }}</dd>
              <dt class="font-medium text-gray-700">API root</dt>
              <dd class="text-gray-600">{{ sysInfo.paths.api_root }}</dd>
              <dt class="font-medium text-gray-700">Deploy root</dt>
              <dd class="text-gray-600">{{ sysInfo.paths.deploy_root }}</dd>
              <dt class="font-medium text-gray-700">Uploads (_fileDB)</dt>
              <dd class="text-gray-600">{{ sysInfo.paths.filedb || '—' }}</dd>
              <dt class="font-medium text-gray-700">Logs</dt>
              <dd class="text-gray-600">{{ sysInfo.paths.logs || '—' }}</dd>
            </dl>
          </div>

          <!-- Connections (secrets masked) -->
          <div class="rounded-md border border-gray-200 bg-gray-50 p-4" *ngIf="sysInfo">
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Connections (secrets masked)</span>
            <dl class="mt-3 grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-4 gap-y-1 text-sm break-all">
              <dt class="font-medium text-gray-700">DB host</dt>
              <dd class="text-gray-600">{{ sysInfo.db.host }}</dd>
              <dt class="font-medium text-gray-700">DB name</dt>
              <dd class="text-gray-600">{{ sysInfo.db.database }}</dd>
              <dt class="font-medium text-gray-700">DB user</dt>
              <dd class="text-gray-600">{{ sysInfo.db.username }}</dd>
              <dt class="font-medium text-gray-700">DB password</dt>
              <dd class="text-gray-600">{{ sysInfo.db.password }}</dd>
              <dt class="font-medium text-gray-700" *ngIf="sysInfo.db.error">DB error</dt>
              <dd class="text-red-600" *ngIf="sysInfo.db.error">{{ sysInfo.db.error }}</dd>
              <dt class="font-medium text-gray-700">SMTP host</dt>
              <dd class="text-gray-600">{{ sysInfo.smtp.host || '—' }} : {{ sysInfo.smtp.port }}</dd>
              <dt class="font-medium text-gray-700">SMTP user</dt>
              <dd class="text-gray-600">{{ sysInfo.smtp.username || '—' }}</dd>
              <dt class="font-medium text-gray-700">SMTP password</dt>
              <dd class="text-gray-600">{{ sysInfo.smtp.password }}</dd>
              <dt class="font-medium text-gray-700">JWT issuer</dt>
              <dd class="text-gray-600">{{ sysInfo.jwt.issuer }}</dd>
              <dt class="font-medium text-gray-700">JWT private key</dt>
              <dd class="text-gray-600">{{ sysInfo.jwt.private_key }}</dd>
              <dt class="font-medium text-gray-700">JWT public key</dt>
              <dd class="text-gray-600">{{ sysInfo.jwt.public_key }}</dd>
            </dl>
          </div>

        </div>
      </mat-tab>

    </mat-tab-group>
  </div>
</div>

<!-- Image picker: browse previously uploaded content images -->
<div
  *ngIf="pickerOpen"
  class="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50"
  (click)="closeImagePicker()"
>
  <div
    class="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
    (click)="$event.stopPropagation()"
  >
    <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200">
      <h3 class="text-base font-bold text-gray-900">Browse uploaded images</h3>
      <button (click)="closeImagePicker()" class="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
    </div>

    <div class="p-5 overflow-y-auto">
      <!-- Upload new images without leaving the dialog -->
      <div class="mb-4 pb-4 border-b border-gray-200">
        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Upload new image</span>
        <span class="ec-hint">Drop or browse to upload — it appears below for selection. JPG, PNG, WebP, GIF.</span>
        <file-pond [options]="pondPickerOptions" (onprocessfile)="onPickerUploaded()"></file-pond>
      </div>

      <p *ngIf="pickerLoading" class="text-sm text-gray-400">Loading…</p>

      <p *ngIf="!pickerLoading && pickerImages.length === 0" class="text-sm text-gray-400">
        No uploaded images yet. Upload one above to get started.
      </p>

      <div *ngIf="!pickerLoading && pickerImages.length > 0" class="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <button
          *ngFor="let img of pickerImages; trackBy: trackByIndex"
          type="button"
          (click)="pickImage(img)"
          class="group border border-gray-200 rounded overflow-hidden hover:border-blue-500 focus:border-blue-500 focus:outline-none"
          [title]="img.name"
        >
          <img [src]="imageUrl(img)" [alt]="img.name" class="w-full h-24 object-cover bg-gray-50" loading="lazy" />
          <span class="block px-2 py-1 text-[11px] text-gray-500 truncate">{{ img.name }}</span>
        </button>
      </div>
    </div>
  </div>
</div>
